import { AssetTypes } from '../types/asset-types.types'
import { CREATE_PLACEMENT_TEMPLATE_DOWNLOAD_DATA } from '../gql/mutations/flowcode'
import {
  CreatePlacementTemplateDownloadDataVariablesType,
  GeneratePlacementTemplateWithFilesProps,
  PlacementTemplateDetails,
  PlacementTemplateSubmitButtonState,
  TemplateBaseConfig,
  TemplateConfigInfo,
  WirePlacementTemplateConfig
} from '../types/placement-template.types'
import { FlowcodeDownloadOptionMimeType } from '@dtx-company/inter-app/src/types/flowcode'
import { createFlowcodeFromFile } from './flowcode'
import { gqlFetcher } from '@dtx-company/inter-app/src/services/gqlFetcher'

import isEmail from 'validator/lib/isEmail'

const MAX_LOGO = 4
const MAX_TEXT = 2
const DEFUALT_FILENAME = 'flowcode_template'

export const createPlacementTemplateDownloadData = (
  variables: CreatePlacementTemplateDownloadDataVariablesType,
  token: string | undefined
): Promise<{ ok: boolean }> => {
  return gqlFetcher(CREATE_PLACEMENT_TEMPLATE_DOWNLOAD_DATA, variables, false, token)
}

export const parsePlacementTemplateConfig = (templateConfigString: string): TemplateConfigInfo => {
  const templateConfigObject = JSON.parse(templateConfigString)
  let logoCount = 0
  Array.from(new Array(MAX_LOGO)).some((_, index) => {
    const key = `logo${index + 1}`
    if (templateConfigObject[key] !== undefined) {
      logoCount = index + 1
    } else {
      // break the loop
      return true
    }
  })

  let textCount = 0
  Array.from(new Array(MAX_TEXT)).some((_, index) => {
    const key = `text${index + 1}`
    if (templateConfigObject[key] !== undefined) {
      textCount = index + 1
    } else {
      // break the loop
      return true
    }
  })

  const baseConfig = templateConfigObject as TemplateBaseConfig
  return {
    ...baseConfig,
    logoCount,
    textCount
  }
}

interface WirePlacementTemplateConfigProps extends Omit<WirePlacementTemplateConfig, 'imageType'> {
  imageType?: FlowcodeDownloadOptionMimeType
}

const ConvertTemplateGeneratorOptions = ({
  studioConfigId,
  url,
  templateOptions,
  imageType = FlowcodeDownloadOptionMimeType.PNG,
  width,
  inchWidth,
  density
}: WirePlacementTemplateConfigProps): WirePlacementTemplateConfig => {
  return {
    studioConfigId,
    url,
    templateOptions,
    imageType,
    width,
    inchWidth,
    density
  }
}

const getLogoFileUrl = async (
  token: string | undefined,
  file?: File
): Promise<string | undefined> => {
  if (!file) return
  const response = await createFlowcodeFromFile(
    {
      data: file,
      assetType: AssetTypes.EXTERNAL_CENTER_IMAGE,
      createBatch: false
    },
    token
  )
  return response.file
}

export const getWirePlacementTemplateConfigWithFiles = async (
  configObject: GeneratePlacementTemplateWithFilesProps
): Promise<WirePlacementTemplateConfig> => {
  const token = configObject.token
  const logo1Url = configObject.wireConfig.logo1File
    ? await getLogoFileUrl(token, configObject.wireConfig.logo1File)
    : undefined
  const logo2Url = configObject.wireConfig.logo2File
    ? await getLogoFileUrl(token, configObject.wireConfig.logo2File)
    : undefined
  const logo3Url = configObject.wireConfig.logo3File
    ? await getLogoFileUrl(token, configObject.wireConfig.logo3File)
    : undefined
  const logo4Url = configObject.wireConfig.logo4File
    ? await getLogoFileUrl(token, configObject.wireConfig.logo4File)
    : undefined
  return ConvertTemplateGeneratorOptions({
    studioConfigId: configObject.studioConfigId,
    url: configObject.shortUrl,
    density: 300,
    imageType:
      configObject.buttonState === PlacementTemplateSubmitButtonState.DOWNLOAD &&
      configObject.imageType
        ? configObject.imageType
        : FlowcodeDownloadOptionMimeType.PDF,
    templateOptions: {
      ...configObject.wireConfig,
      logo1: logo1Url ?? configObject.wireConfig.logo1,
      logo2: logo2Url ?? configObject.wireConfig.logo2,
      logo3: logo3Url ?? configObject.wireConfig.logo3,
      logo4: logo4Url ?? configObject.wireConfig.logo4
    }
  })
}

export const getPlacementTemplateDetails = async ({
  wireConfig,
  studioConfigId,
  shortUrl,
  token
}: GeneratePlacementTemplateWithFilesProps): Promise<PlacementTemplateDetails> => {
  const { url, templateOptions: templateOptionsWithFileUrls } =
    await getWirePlacementTemplateConfigWithFiles({
      wireConfig,
      studioConfigId,
      shortUrl,
      token
    })
  const templateOptions = await import('lodash/pick')
    .then(mod => mod.default)
    .then(pick => {
      return pick(templateOptionsWithFileUrls, [
        'template',
        'logo1',
        'logo2',
        'logo3',
        'logo4',
        'text1',
        'text2'
      ])
    })
  return {
    templateOptions,
    url,
    studioConfigId
  }
}

export const checkEmailListInvalid = (emails: string): boolean => {
  let isInvalid = false
  if (!emails) isInvalid = true
  if (emails.split(',').some(address => !isEmail(address.trim()))) isInvalid = true
  return isInvalid
}

export const getPlacementTemplateInputsString = (inputs: TemplateBaseConfig): string => {
  const templateInputs = {
    logo1: inputs.logo1,
    logo2: inputs.logo2,
    logo3: inputs.logo3,
    logo4: inputs.logo4,
    text1: inputs.text1 ? inputs.text1.text : undefined,
    text2: inputs.text2 ? inputs.text2.text : undefined
  }
  // JSON stringify will remove any undefined fields
  return JSON.stringify(templateInputs)
}

// this helper function is used to determine the max width of a template
// if the width is >= to the height, maxSideDimension is returned
// however, if the height is < the width, the maxWidth is calculated so that the
// height will be equivalent to maxSideDimension
export const getTemplateMaxWidth = (viewBox: string, maxSideDimension: number): number => {
  const viewBoxVals = viewBox.split(' ')
  let aspectRatio = 1
  // viewBox is supposed to be of shape "x1 y1 x2 y2", skip otherwise
  if (viewBoxVals.length === 4) {
    const width = parseFloat(viewBoxVals[2]) - parseFloat(viewBoxVals[0])
    const height = parseFloat(viewBoxVals[3]) - parseFloat(viewBoxVals[1])
    aspectRatio = width / height
  }
  return aspectRatio < 1 ? Math.ceil(maxSideDimension * aspectRatio) + 1 : maxSideDimension
}
